import React from 'react';
import {
  ChevronDown,
  FileDown,
  History,
  Mail,
  Maximize2,
  Minimize2,
  Plus,
  Trash2,
  X,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/utils';
import { RiqueIcon } from '@/components/Rique/RiqueIcon';
import type { RiqueAlert, RiqueBriefing, RiqueConversation, RiqueMessage } from '@/lib/riqueApi';
import { RiqueMessageList } from '@/components/Rique/RiqueMessageList';
import { RiqueComposer } from '@/components/Rique/RiqueComposer';
import { exportConversationPdf } from '@/lib/riqueExport';
import { toast } from 'react-hot-toast';

interface RiqueChatPanelProps {
  expanded?: boolean;
  companies: Array<{ id: string; name: string }> | undefined;
  companyId: string;
  setCompanyId: (id: string) => void;
  showHistory: boolean;
  setShowHistory: (show: boolean) => void;
  conversations: RiqueConversation[];
  activeId: string | null;
  setActiveId: (id: string) => void;
  activeConversation: RiqueConversation | undefined;
  messages: RiqueMessage[];
  streaming: string;
  loadingReply: boolean;
  unavailable: boolean;
  alerts: RiqueAlert[] | undefined;
  briefing?: RiqueBriefing | null;
  routeSuggestions: string[];
  lastAssistantText?: string;
  onSend: (text: string) => Promise<void>;
  onNewConversation: () => Promise<unknown>;
  onDeleteConversation?: (conversationId: string) => Promise<void>;
  onDeleteAllConversations?: () => Promise<void>;
  onMinimize?: () => void;
  onClose: () => void;
  onToggleExpand?: () => void;
  onEmailClick?: () => void;
  userName?: string;
  companyName?: string;
}

export const RiqueChatPanel: React.FC<RiqueChatPanelProps> = ({
  expanded = false,
  companies,
  companyId,
  setCompanyId,
  showHistory,
  setShowHistory,
  conversations,
  activeId,
  setActiveId,
  activeConversation,
  messages,
  streaming,
  loadingReply,
  unavailable,
  alerts,
  briefing,
  routeSuggestions,
  lastAssistantText,
  onSend,
  onNewConversation,
  onDeleteConversation,
  onDeleteAllConversations,
  onMinimize,
  onClose,
  onToggleExpand,
  onEmailClick,
  userName,
  companyName,
}) => {
  const [deletingId, setDeletingId] = React.useState<string | null>(null);
  const [clearingAll, setClearingAll] = React.useState(false);

  const selectConversation = (id: string) => {
    setActiveId(id);
    setShowHistory(false);
  };

  const handleExportPdf = () => {
    if (messages.length === 0) {
      toast.error('Não há mensagens para exportar.');
      return;
    }
    try {
      exportConversationPdf({
        title: activeConversation?.title ?? 'Conversa Rique.Ia',
        messages,
        authorName: userName,
        companyName: companyName ?? activeConversation?.company?.name ?? undefined,
      });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Falha ao exportar PDF.');
    }
  };

  const handleDeleteOne = async (e: React.MouseEvent, conversationId: string) => {
    e.stopPropagation();
    if (!onDeleteConversation) return;
    if (!window.confirm('Excluir esta conversa? Esta ação não pode ser desfeita.')) return;
    setDeletingId(conversationId);
    try {
      await onDeleteConversation(conversationId);
      toast.success('Conversa excluída.');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Falha ao excluir conversa.');
    } finally {
      setDeletingId(null);
    }
  };

  const handleDeleteAll = async () => {
    if (!onDeleteAllConversations || conversations.length === 0) return;
    if (!window.confirm('Excluir todo o histórico de conversas? Esta ação não pode ser desfeita.')) return;
    setClearingAll(true);
    try {
      await onDeleteAllConversations();
      toast.success('Histórico limpo.');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Falha ao limpar histórico.');
    } finally {
      setClearingAll(false);
    }
  };

  const canExport = messages.length > 0 && activeId;

  return (
    <div
      className={cn(
        'flex flex-col overflow-hidden bg-card',
        expanded ? 'h-full w-full rounded-xl' : 'h-full w-full rounded-xl border border-border/80 shadow-2xl'
      )}
      role="dialog"
      aria-label="Chat Rique.Ia"
    >
      <header
        className={cn(
          'flex shrink-0 flex-col gap-2 border-b border-border/60 px-3 py-2.5',
          'bg-gradient-to-r from-primary/10 via-emerald-500/5 to-transparent',
          expanded && 'px-4 py-3'
        )}
      >
        <div className="flex items-center gap-2">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white p-0.5 shadow-sm ring-1 ring-border/40">
            <RiqueIcon className="h-full w-full" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold leading-tight text-foreground">Rique.Ia</p>
            <p className="text-[10px] text-muted-foreground">Analista · PortalFly & Zeh Motoca</p>
          </div>
          {canExport ? (
            <>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={handleExportPdf}
                aria-label="Exportar PDF"
                title="Exportar PDF Zeh Motoca"
              >
                <FileDown className="h-4 w-4" />
              </Button>
              {onEmailClick ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={onEmailClick}
                  aria-label="Enviar por e-mail"
                  title="Enviar por e-mail"
                >
                  <Mail className="h-4 w-4" />
                </Button>
              ) : null}
            </>
          ) : null}
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={() => setShowHistory(!showHistory)}
            aria-label="Histórico de conversas"
          >
            <History className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={() => void onNewConversation()}
            aria-label="Nova conversa"
          >
            <Plus className="h-4 w-4" />
          </Button>
          {onToggleExpand ? (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={onToggleExpand}
              aria-label={expanded ? 'Reduzir janela' : 'Expandir conversa'}
              title={expanded ? 'Reduzir' : 'Expandir'}
            >
              {expanded ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
            </Button>
          ) : null}
          {!expanded && onMinimize ? (
            <Button type="button" variant="ghost" size="icon" className="h-8 w-8" onClick={onMinimize} aria-label="Minimizar">
              <ChevronDown className="h-4 w-4" />
            </Button>
          ) : null}
          <Button type="button" variant="ghost" size="icon" className="h-8 w-8" onClick={onClose} aria-label="Fechar">
            <X className="h-4 w-4" />
          </Button>
        </div>
        <select
          className="h-8 w-full rounded-md border border-input bg-background/80 px-2 text-xs text-foreground"
          value={companyId}
          onChange={(e) => setCompanyId(e.target.value)}
          aria-label="Empresa em contexto"
        >
          <option value="">Todas as empresas</option>
          {(companies ?? []).map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
      </header>

      {unavailable ? (
        <div className="shrink-0 border-b border-amber-500/30 bg-amber-500/5 px-3 py-1.5 text-[11px] text-amber-900 dark:text-amber-100">
          Indisponível: configure OPENAI_API_KEY na API.
        </div>
      ) : null}

      {(alerts?.length ?? 0) > 0 ? (
        <div className={cn('max-h-24 shrink-0 overflow-y-auto border-b border-border/50 px-3 py-2', expanded && 'max-h-32 px-4')}>
          <p className="mb-1 text-[10px] font-semibold uppercase text-muted-foreground">Alertas</p>
          <ul className="space-y-1">
            {alerts!.slice(0, expanded ? 5 : 3).map((a) => (
              <li key={a.id} className="text-[10px] text-foreground">
                <span className="font-medium">{a.titulo}:</span> {a.mensagem}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <div className="relative flex min-h-0 flex-1 flex-col">
        {showHistory && (
          <div className="absolute inset-0 z-10 flex flex-col bg-card/98 backdrop-blur-sm">
            <div className="flex items-center justify-between border-b border-border/80 px-3 py-2">
              <span className="text-xs font-medium text-muted-foreground">Conversas</span>
              <div className="flex items-center gap-1">
                {onDeleteAllConversations && conversations.length > 0 ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 gap-1 text-xs text-destructive hover:text-destructive"
                    onClick={() => void handleDeleteAll()}
                    disabled={clearingAll}
                  >
                    <Trash2 className="h-3 w-3" aria-hidden />
                    Limpar tudo
                  </Button>
                ) : null}
                <Button type="button" variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setShowHistory(false)}>
                  Voltar
                </Button>
              </div>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto p-2">
              {conversations.length === 0 ? (
                <p className="px-2 py-4 text-center text-xs text-muted-foreground">Nenhuma conversa ainda.</p>
              ) : (
                conversations.map((c) => (
                  <div
                    key={c.id}
                    className={cn(
                      'group mb-1 flex items-stretch gap-0.5 rounded-lg transition-colors',
                      activeId === c.id ? 'bg-primary/10' : 'hover:bg-muted/60'
                    )}
                  >
                    <button
                      type="button"
                      onClick={() => selectConversation(c.id)}
                      className={cn(
                        'min-w-0 flex-1 rounded-lg px-2.5 py-2 text-left text-xs',
                        activeId === c.id ? 'text-foreground' : 'text-muted-foreground'
                      )}
                    >
                      <span className="line-clamp-2 font-medium">{c.title}</span>
                      {c.company?.name ? (
                        <span className="mt-0.5 block truncate text-[10px] opacity-70">{c.company.name}</span>
                      ) : null}
                    </button>
                    {onDeleteConversation ? (
                      <button
                        type="button"
                        onClick={(e) => void handleDeleteOne(e, c.id)}
                        disabled={deletingId === c.id || clearingAll}
                        className={cn(
                          'shrink-0 rounded-lg px-2 text-muted-foreground opacity-0 transition-opacity',
                          'hover:bg-destructive/10 hover:text-destructive group-hover:opacity-100 focus-visible:opacity-100',
                          deletingId === c.id && 'opacity-100'
                        )}
                        aria-label={`Excluir conversa ${c.title}`}
                        title="Excluir conversa"
                      >
                        <Trash2 className="h-3.5 w-3.5" aria-hidden />
                      </button>
                    ) : null}
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        <div className={cn('border-b border-border/50 px-3 py-1.5', expanded && 'px-4 py-2')}>
          <p className="truncate text-xs text-muted-foreground">{activeConversation?.title ?? 'Nova conversa'}</p>
        </div>

        <RiqueMessageList
          messages={messages}
          streamingContent={streaming}
          isLoading={loadingReply}
          compact={!expanded}
          expanded={expanded}
          briefing={messages.length === 0 && !streaming && !loadingReply ? briefing : undefined}
        />
        <RiqueComposer
          disabled={unavailable}
          onSend={onSend}
          lastAssistantText={lastAssistantText}
          compact={!expanded}
          suggestions={routeSuggestions}
        />
      </div>
    </div>
  );
};
